import {
	TextHeading,
	TextValue,
} from '@dealstack/frontend/shared/components/Documents/FormFields/styled';
import { FormFieldListValue } from '@dealstack/frontend/shared/components/Documents/FormFields/types';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { Badge, Flex, FlexProps } from '@mantine/core';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import {
	DOCUMENT_LIBRARY_ELEMENT_ID,
	makeComplexDataTestId,
} from '@dealstack/shared/tests/testElementIds';

interface Props {
	item: FormFieldListValue;
	isEditing?: boolean;
	removeItem?: (item: FormFieldListValue) => void;
}

/**
 * ListField component displays a list item with an icon, value, optional subtext, and left badge.
 */
export const ListField = ({ item, isEditing = false, removeItem }: Props) => {
	const isEditingAndNotEditable = isEditing && item.badge !== 'link';
	return (
		<ListFieldContainer
			data-testid={makeComplexDataTestId(DOCUMENT_LIBRARY_ELEMENT_ID.LIST_ITEM, item.id)}
			opacity={isEditingAndNotEditable ? 0.5 : 1}
			justify={isEditing ? 'space-between' : 'flex-start'}
			w="100%"
			$shouldHover={!!item.subText}
		>
			<Flex gap="0.5rem" align="center">
				<Icon variant={item.icon ?? 'user'} size="lg" />
				<Flex direction="column" gap="0.25rem">
					<TextValue>{item.value}</TextValue>
					{item.subText && (
						<Link to={item.url ?? ''} style={{ textDecoration: 'none' }}>
							<TextHeading $shouldHover={!!item.url}>{item.subText}</TextHeading>
						</Link>
					)}
				</Flex>

				{item.badge && (
					<Flex gap="0.5rem" wrap="wrap">
						<Badge c="dark.4" color="gray.4" fw={400} variant="outline" size="md">
							{item.badge}
						</Badge>
					</Flex>
				)}
			</Flex>
			{isEditing && !isEditingAndNotEditable && (
				<div
					className="close-icon"
					data-testid={makeComplexDataTestId(
						DOCUMENT_LIBRARY_ELEMENT_ID.REMOVE_LIST_ITEM,
						item.id
					)}
					style={{ marginRight: '0.5rem' }}
					onClick={() => removeItem?.(item)}
				>
					<Icon variant="close-v3" size="sm" style={{ cursor: 'pointer' }} />
				</div>
			)}
		</ListFieldContainer>
	);
};

const ListFieldContainer = styled(Flex)<FlexProps & { $shouldHover?: boolean }>`
	gap: 0.5rem;
	align-items: center;
	padding: ${({ $shouldHover }) => ($shouldHover ? '0.25rem' : '0')};

	${({ $shouldHover, theme }) =>
		$shouldHover &&
		css`
			&:hover {
				background-color: ${theme.v3.colors.gray[0]};
				border-radius: 0.25rem;
				.close-icon svg {
					color: red !important;
				}
			}
		`}
`;
